import { inject, Injectable } from '@angular/core';
import { type Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { EventCardApi, type EventCardWithCommentsQuery_EventCard } from '../../api/event-card/event-card.api';
import { GoalCardApi, type GoalCardWithCommentsQuery_GoalCard } from '../../api/goal-card/goal-card.api';
import { type CardTaskStatusEnum, EventCardStatusEnum, GoalCardStatusEnum } from '../../schema/schema-enums';
import { isGoalCardId } from '../../utils/typename.utils';
import { LogService } from '../log/log.service';
import { TaskService } from '../task/task.service';
import { EventCardService } from './event-card/event-card.service';
import { GoalCardService } from './goal-card/goal-card.service';

@Injectable({
  providedIn: 'root',
})
export class WorkspaceCardService {
  private goalCardApi = inject(GoalCardApi);
  private eventCardApi = inject(EventCardApi);
  private goalService = inject(GoalCardService);
  private eventService = inject(EventCardService);
  private logService = inject(LogService);
  private taskService = inject(TaskService);

  watchCardWithComments(
    cardId: string,
  ): Observable<GoalCardWithCommentsQuery_GoalCard | EventCardWithCommentsQuery_EventCard> {
    return isGoalCardId(cardId)
      ? this.goalCardApi.watchGoalCardWithComments(cardId as string)
      : this.eventCardApi.watchEventCardWithComments(cardId as string);
  }

  watchCardLogs(cardId: string) {
    return isGoalCardId(cardId)
      ? this.logService.watchGoalCardLogs([cardId])
      : this.logService.watchEventCardLogs([cardId]);
  }

  watchLinkedCards(cardId: string) {
    return isGoalCardId(cardId)
      ? this.goalService.watchLinkedCards(cardId)
      : this.eventService.watchLinkedCards(cardId);
  }

  watchNonArchivedCards(workspaceId: string) {
    const cardFilter = { workspaceIds: [workspaceId] };

    return this.goalService.watchGoalCards(cardFilter).pipe(
      mergeMap((goalViews) => {
        return this.eventService
          .watchEventCards(cardFilter)
          .pipe(
            map((eventViews) => [
              ...goalViews.filter((goal) => goal.goalCardStatus !== GoalCardStatusEnum.ARCHIVED),
              ...eventViews.filter((event) => event.eventCardStatus !== EventCardStatusEnum.ARCHIVED),
            ]),
          );
      }),
    );
  }

  watchAllTasksWithUsers(cardId: string, statuses: CardTaskStatusEnum[]) {
    return isGoalCardId(cardId)
      ? this.taskService.watchGoalTasksWithUsers([cardId], statuses)
      : this.taskService.watchEventTasksWithUsers([cardId], statuses);
  }
}
